// 询价价格匹配 API
import { request } from '@/utils/http'
import type {
  PriceFclListVO,
  PriceLclListVO,
  PricePrePortVO,
  PriceLastMileVO,
  PriceAirListVO
} from '@/api/sass/rate-management/types'
import type { ProductRateDetailVO } from '@/api/sass/rate-query/ecommerce'

// 询价匹配请求 DTO
export interface InquiryMatchRequest {
  type: number | null // 询价类型: 1-FCL, 2-LCL, 3-AIR, 4-PRE_PORT, 5-LAST_MILE
  trunkLineEnable?: boolean | null
  prePortEnable?: boolean | null
  lastMileEnable?: boolean | null
  polId?: string | null
  podId?: string | null
  potId?: string | null
  seaEq?: string | null // 箱型箱量 eg: 1*20GP+2*40GP
  /** 计费重(KG)：单位「计费重」时 单价×计费重 */
  chargeableWeight?: number | null
  /** 重量(KG)：单位「重量」时 单价×实际毛重 */
  weight?: number | null
  /** 体积(CBM)：单位「体积」时 单价×体积 */
  cubage?: number | null
  etd?: string | null
  etdType?: string | null // ETD时间-类型: date-具体日期, range-日期区间
  etdStart?: string | null // ETD-区间开始（etdType=range时填写）
  etdEnd?: string | null // ETD-区间结束（etdType=range时填写）
  eta?: string | null

  // 装货点地址相关（港前匹配需要）- 使用5级地址结构
  stuffingCountryId?: string | null
  stuffingProvinceId?: string | null
  stuffingCityId?: string | null
  stuffingDistrictId?: string | null
  stuffingStreetId?: string | null
  stuffingVillageId?: string | null

  // 配送地址相关（尾程匹配需要）
  deliveryAddressType?: number | null // 配送地址类型: 1-第三方地址, 2-亚马逊仓库, 3-易仓
  deliveryCountryId?: string | null
  deliveryProvinceId?: string | null
  deliveryCityId?: string | null
  deliveryDistrictId?: string | null
  warehouseId?: string | null
  fbaCode?: string | null

  // 货好时间相关
  inquiryCreateTime?: string | null // 询价创建时间（用于计算货好时间）
  cargoReadyTimeType?: string | null // 货好时间类型: date-具体日期, range-时间范围
  cargoReadyTimeRange?: number | null // 货好时间范围: 1-一周内, 2-二周内, 3-一个月内, 4-一个月以上, 5-时间未知
  cargoReadyTimeDate?: string | null // 货好时间具体日期（当type=date时使用）

  // 干线相关
  cargoReadyTimeStart?: string | null
  cargoReadyTimeEnd?: string | null
  deadLine?: string | null

  carrierId?: string | null
  /** 船公司ID列表（多选，优先于单值 carrierId） */
  carrierIds?: string[] | null
  directTransit?: number | null // 直达/中转: 1-直达, 2-中转

  /** 起运地国家ID（电商匹配：港口为空时兜底） */
  countryOriginId?: string | null
  /** 起运地城市ID */
  cityOriginId?: string | null
  /** 目的地国家ID */
  countryDestId?: string | null
  /** 目的地城市ID */
  cityDestId?: string | null

  /** 运价状态列表；为空默认查发布（整箱/拼箱匹配用） */
  priceStatusList?: number[] | null
  /** 服务类型；空运有值时按服务类型过滤干线 */
  serviceType?: string | null
  /** 空运命中密度等级，如 0 < 1CBM < 166 */
  hitDensityLevel?: string | null
  /** 空运命中重量段，如 p2000 */
  hitSection?: string | null
  /** 排序: lowestUsdPriceFirst(默认) | latestBeginDateFirst */
  sortType?: string | null

  pageNum?: number | null
  pageSize?: number | null
}

/** 联运组合（干线 + 港前 + 尾程），对齐后端 LinkedRateComboVO */
export interface LinkedRateComboVO {
  trunkRate?: PriceFclListVO | null
  prePortRate?: (PricePrePortVO & Record<string, any>) | null
  lastMileRate?: (PriceLastMileVO & Record<string, any>) | null
  /** 组合价格汇总 */
  priceSummary?: string | null
  /** USD 总价 */
  totalUsdPrice?: number | null
  /** 干线价格汇总 */
  trunkPriceSummary?: string | null
  /** 港前价格汇总 */
  prePortPriceSummary?: string | null
  /** 尾程价格汇总 */
  lastMilePriceSummary?: string | null
}

export interface LinkedRateComboPage {
  records: LinkedRateComboVO[]
  total: number
  size: number
  current: number
  pages?: number
}

/**
 * 匹配整箱推荐运价
 */
export function matchInquiryPrice(params: InquiryMatchRequest) {
  return request.post<LinkedRateComboPage>('/price/inquiry/match/fcl/price', params)
}

/** 拼箱联运组合（LCL干线 + 港前 + 尾程） */
export interface LclRateComboVO {
  trunkRate?: (PriceLclListVO & Record<string, any>) | null
  prePortRate?: (PricePrePortVO & Record<string, any>) | null
  lastMileRate?: (PriceLastMileVO & Record<string, any>) | null
  priceSummary?: string | null
  totalUsdPrice?: number | null
  trunkPriceSummary?: string | null
  prePortPriceSummary?: string | null
  lastMilePriceSummary?: string | null
}

export interface LclRateComboPage {
  records: LclRateComboVO[]
  total: number
  size: number
  current: number
  pages?: number
}

/**
 * 匹配拼箱推荐运价
 * POST /price/inquiry/match/lcl/price
 */
export function matchInquiryLclPrice(params: InquiryMatchRequest) {
  return request.post<LclRateComboPage>('/price/inquiry/match/lcl/price', params)
}

/** 空运联运组合（对齐后端 LinkedAirRateComboVO） */
export interface LinkedAirRateComboVO {
  trunkRate?: PriceAirListVO | null
  prePortRate?: PricePrePortVO | null
  lastMileRate?: PriceLastMileVO | null
  priceSummary?: string
  totalUsdPrice?: number
  trunkPriceSummary?: string
  prePortPriceSummary?: string
  lastMilePriceSummary?: string
}

export interface LinkedAirRateComboPage {
  records: LinkedAirRateComboVO[]
  total: number
  size: number
  current: number
  pages?: number
}

/** 空运联运组合匹配 */
export function matchInquiryAirPrice(params: InquiryMatchRequest) {
  return request.post<LinkedAirRateComboPage>('/price/inquiry/match/air/price', params)
}

/** 电商运价（班期最新价）分页，对齐后端 LatestPriceVO */
export interface LatestPriceContainerPrice {
  detailId?: number | string
  ctnType?: string
  prodStatus?: number
  basePrice?: number
  totalPrice?: number
  currency?: string
}

export interface LatestPriceVO {
  scheduleId?: number | string
  freightId?: string
  carrierCode?: string
  vesselName?: string
  voyNo?: string
  carrierRoute?: string
  polCode?: string
  polName?: string
  podCode?: string
  podName?: string
  polEtd?: string
  podEta?: string
  voyDays?: number
  isTransit?: boolean
  validFrom?: string
  validTo?: string
  transTerm?: string
  shelfStatus?: number
  containerPrices?: LatestPriceContainerPrice[] | null
  /** 分币种价格汇总，如 2000USD + 300CNY（含海运费×箱量 + 附加费） */
  priceSummary?: string
  /** 本条运价费率详情（海运费/附加费/退改费/滞期费），挂在每条 VO 上 */
  rateDetail?: ProductRateDetailVO | null
  [key: string]: unknown
}

export interface LatestPricePage {
  records: LatestPriceVO[]
  total: number
  size: number
  current: number
  pages?: number
}

/**
 * 电商运价匹配响应，对齐后端 EcommercePriceMatchResponse
 * 费率详情已下沉到 page.records[].rateDetail，不再挂在响应根上
 */
export interface EcommercePriceMatchResponse {
  /** 班期运价分页结果 */
  page: LatestPricePage
}

/**
 * 匹配电商运价
 * POST /price/inquiry/match/ecommerce/price
 */
export function matchInquiryEcommercePrice(params: InquiryMatchRequest) {
  return request.post<EcommercePriceMatchResponse>('/price/inquiry/match/ecommerce/price', params)
}

/** 从组合分页结果拆出三段去重列表（创建页等仍按段表格展示时用） */
export function flattenLinkedCombos(page: LinkedRateComboPage | null | undefined) {
  const records = page?.records || []
  const fclMap = new Map<string, PriceFclListVO>()
  const preMap = new Map<string, NonNullable<LinkedRateComboVO['prePortRate']>>()
  const lastMap = new Map<string, NonNullable<LinkedRateComboVO['lastMileRate']>>()
  for (const c of records) {
    if (c.trunkRate?.id != null) fclMap.set(String(c.trunkRate.id), c.trunkRate)
    if (c.prePortRate?.id != null) preMap.set(String(c.prePortRate.id), c.prePortRate)
    if (c.lastMileRate?.id != null) lastMap.set(String(c.lastMileRate.id), c.lastMileRate)
  }
  return {
    fclMatches: [...fclMap.values()],
    prePortMatches: [...preMap.values()],
    lastMileMatches: [...lastMap.values()]
  }
}

/** 从拼箱组合分页结果拆出三段去重列表 */
export function flattenLclLinkedCombos(page: LclRateComboPage | null | undefined) {
  const records = page?.records || []
  const lclMap = new Map<string, NonNullable<LclRateComboVO['trunkRate']>>()
  const preMap = new Map<string, NonNullable<LclRateComboVO['prePortRate']>>()
  const lastMap = new Map<string, NonNullable<LclRateComboVO['lastMileRate']>>()
  for (const c of records) {
    if (c.trunkRate?.id != null) lclMap.set(String(c.trunkRate.id), c.trunkRate)
    if (c.prePortRate?.id != null) preMap.set(String(c.prePortRate.id), c.prePortRate)
    if (c.lastMileRate?.id != null) lastMap.set(String(c.lastMileRate.id), c.lastMileRate)
  }
  return {
    lclMatches: [...lclMap.values()],
    prePortMatches: [...preMap.values()],
    lastMileMatches: [...lastMap.values()]
  }
}

/** 从空运组合分页结果拆出三段去重列表 */
export function flattenLinkedAirCombos(page: LinkedAirRateComboPage | null | undefined) {
  const records = page?.records || []
  const airMap = new Map<string, PriceAirListVO>()
  const preMap = new Map<string, NonNullable<LinkedAirRateComboVO['prePortRate']>>()
  const lastMap = new Map<string, NonNullable<LinkedAirRateComboVO['lastMileRate']>>()
  for (const c of records) {
    if (c.trunkRate?.id != null) airMap.set(String(c.trunkRate.id), c.trunkRate)
    if (c.prePortRate?.id != null) preMap.set(String(c.prePortRate.id), c.prePortRate)
    if (c.lastMileRate?.id != null) lastMap.set(String(c.lastMileRate.id), c.lastMileRate)
  }
  return {
    airMatches: [...airMap.values()],
    prePortMatches: [...preMap.values()],
    lastMileMatches: [...lastMap.values()]
  }
}
